/**
 * HomePage – the daily hub.
 *
 * Top: greeting + current level label + streak.
 * Main actions: Start Free Talk / Start Guided Scenario.
 * Lower: Today's recommendation, quick links to Vocabulary + Profile, recent sessions.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChatCircleDots,
  BookOpenText,
  Flame,
  Clock,
  CaretRight,
  SignOut,
  User as UserIcon,
  Trophy,
  Translate,
  ArrowClockwise,
  ArrowSquareOut,
  WarningCircle,
  Repeat,
  Crown,
  Ticket,
  GraduationCap,
  Compass,
} from '@phosphor-icons/react';
import { http, apiErrorMessage } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isNative } from '@/lib/native';
import WordOfTheDay from '@/components/WordOfTheDay';
import SoundOfTheWeek from '@/components/SoundOfTheWeek';
import FirstRunTour from '@/components/FirstRunTour';

export default function HomePage() {
  const { user, logout, patchUser } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showTour, setShowTour] = useState(false);

  // Show the first-run tour for users whose tutorial isn't marked complete.
  useEffect(() => {
    if (user && typeof user === 'object' && user.tutorial_completed === false) {
      const t = setTimeout(() => setShowTour(true), 600);
      return () => clearTimeout(t);
    }
  }, [user]);

  async function finishTour() {
    setShowTour(false);
    try {
      const { data } = await http.post('/me/tutorial-complete');
      patchUser(data);
    } catch { /* non-fatal */ }
  }

  // Detect preview-iframe so we can nudge the user to open in a new tab
  // BEFORE they hit a mic permission wall inside a session. On native
  // (Capacitor iOS/Android) we always bypass this banner — the OS handles
  // permissions, no iframe involved.
  const inIframe = (() => {
    if (isNative()) return false;
    try { return window.self !== window.top; } catch { return true; }
  })();

  function openInNewTab() {
    try { window.open(window.location.origin + '/home', '_blank', 'noopener,noreferrer'); }
    catch { /* popup blocked */ }
  }

  useEffect(() => {
    (async () => {
      try {
        const [p, s] = await Promise.all([http.get('/progress'), http.get('/sessions')]);
        setProgress(p.data);
        setSessions(s.data.slice(0, 5));
      } catch (e) {
        setError(apiErrorMessage(e));
      }
    })();
  }, []);

  async function startFreeTalk() {
    // Phase 2 — route through the Prep Screen. PrepScreen does the POST to
    // /api/session/start once the learner picks focus words.
    navigate('/prep?mode=free_talk');
  }

  /** Open the Teacher Mode v2 daily lesson Blackboard. */
  async function startTeacherSession() {
    navigate('/teacher/today');
  }

  const directionLabel = user?.practice_direction === 'es_to_en' ? 'English' : 'Spanish';

  return (
    <div className="lf-shell flex flex-col px-6 py-8" data-testid="home-page">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Practicing</div>
          <div className="text-2xl font-bold">{directionLabel}</div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/guide"
            className="grid h-11 w-11 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)] text-[var(--lf-text)]"
            title="Quick reference guide"
            data-testid="home-guide-link"
          >
            <Compass size={18} weight="duotone" />
          </Link>
          {user?.role === 'admin' ? (
            <Link to="/admin/promo" className="grid h-11 w-11 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)] text-[var(--lf-primary)]" title="Admin: promo codes" data-testid="home-admin-link">
              <Ticket size={18} weight="duotone" />
            </Link>
          ) : null}
          <Link to="/profile" className="grid h-11 w-11 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)] text-[var(--lf-text)]" data-testid="home-profile-link">
            {user?.picture ? (
              <img src={user.picture} alt="avatar" className="h-full w-full rounded-full object-cover" />
            ) : (
              <UserIcon size={20} weight="duotone" />
            )}
          </Link>
          <button
            onClick={async () => { await logout(); navigate('/', { replace: true }); }}
            className="grid h-11 w-11 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]"
            title="Sign out"
            data-testid="home-logout-btn"
          >
            <SignOut size={18} />
          </button>
        </div>
      </div>

      {/* Iframe-preview banner: prompts the user to pop the app into a full
          tab BEFORE they hit the mic permission wall inside a session. */}
      {inIframe ? (
        <div
          className="mt-5 rounded-2xl border border-[var(--lf-accent)] bg-[#FFF6DB] px-4 py-3 text-sm"
          data-testid="home-iframe-banner"
        >
          <div className="flex items-start gap-2">
            <WarningCircle size={18} weight="fill" className="mt-0.5 text-[#8A6B0F]" />
            <div className="flex-1 text-[#5C4809]">
              <div className="font-semibold">Open in a new tab to use your mic</div>
              <div className="mt-0.5 text-[#6A5410]">
                This preview window blocks the microphone prompt. One tap fixes it.
              </div>
              <button
                onClick={openInNewTab}
                className="lf-btn lf-btn-primary mt-3 w-full py-2 text-sm"
                data-testid="home-open-in-new-tab-btn"
              >
                <ArrowSquareOut size={14} weight="bold" /> Open LingoFlow
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Plan / upgrade banner — always shown so Pro users see their status
          and free users see a friendly nudge. */}
      {user?.level_kind === 'newcomer' && !user?.newcomer_completed ? (
        !user?.newcomer_flashcards_completed ? (
          <button
            onClick={() => navigate('/newcomer')}
            className="mt-5 w-full rounded-2xl border border-[var(--lf-primary)] bg-gradient-to-br from-[#FFF5EE] to-[#FFE9DA] px-4 py-4 text-left transition-all hover:-translate-y-0.5"
            data-testid="home-newcomer-cta"
          >
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-[var(--lf-primary)] text-white text-xl">
                🌱
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-[var(--lf-primary)]">Step 1 · Flashcards</div>
                <div className="mt-0.5 font-bold text-[var(--lf-text)]">Continue your starter flashcards</div>
                <div className="text-xs text-[var(--lf-text-muted)]">
                  {progress?.newcomer?.mastered ?? 0} of {progress?.newcomer?.total_cards ?? 50} cards mastered
                </div>
              </div>
              <CaretRight size={18} className="text-[var(--lf-primary)]" />
            </div>
          </button>
        ) : (
          <button
            onClick={() => navigate('/foundations')}
            className="mt-5 w-full rounded-2xl border border-[var(--lf-primary)] bg-gradient-to-br from-[#FFF5EE] to-[#FFE9DA] px-4 py-4 text-left transition-all hover:-translate-y-0.5"
            data-testid="home-foundations-cta"
          >
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-[var(--lf-primary)] text-white text-xl">
                🔊
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-[var(--lf-primary)]">Step 2 · Sound Foundations</div>
                <div className="mt-0.5 font-bold text-[var(--lf-text)]">Master the 5 key sounds</div>
                <div className="text-xs text-[var(--lf-text-muted)]">
                  {progress?.newcomer?.sounds_completed ?? 0} of {progress?.newcomer?.total_sounds ?? 5} lessons done
                </div>
              </div>
              <CaretRight size={18} className="text-[var(--lf-primary)]" />
            </div>
          </button>
        )
      ) : null}
      {progress?.plan?.is_pro ? (
        <div className="mt-5 rounded-2xl border border-[var(--lf-primary)] bg-[#FFF5EE] px-4 py-3 text-sm" data-testid="home-plan-banner-pro">
          <div className="flex items-center gap-2">
            <Crown size={18} weight="fill" className="text-[var(--lf-primary)]" />
            <div className="flex-1">
              <div className="font-semibold">{progress.plan.label}</div>
              <div className="text-xs text-[var(--lf-text-muted)]">
                {progress.plan.plan_expires_at
                  ? `Renews on ${new Date(progress.plan.plan_expires_at).toLocaleDateString()}`
                  : 'Lifetime access — enjoy!'}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => navigate('/pricing')}
          className="mt-5 w-full rounded-2xl border border-[var(--lf-primary)] bg-gradient-to-br from-[#FFF5EE] to-[#FFE9DA] px-4 py-3 text-left text-sm transition-all hover:-translate-y-0.5"
          data-testid="home-upgrade-banner"
        >
          <div className="flex items-center gap-2">
            <Crown size={18} weight="fill" className="text-[var(--lf-primary)]" />
            <div className="flex-1">
              <div className="font-bold text-[var(--lf-text)]">Upgrade to LingoFlow Pro</div>
              <div className="text-xs text-[var(--lf-text-muted)]">Unlimited sessions, all voices, from $9.99/mo</div>
            </div>
            <CaretRight size={16} className="text-[var(--lf-primary)]" />
          </div>
        </button>
      )}

      {/* Hero stats */}
      <motion.div
        className="lf-card mt-6 p-5"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-end justify-between">
          <div>
            <div className="text-sm text-[var(--lf-text-muted)]">Hello,</div>
            <div className="text-2xl font-bold" data-testid="home-greeting">{user?.name || 'there'} 👋</div>
          </div>
          <div className="lf-chip lf-chip-primary" data-testid="home-level-chip">
            <Trophy size={12} weight="fill" /> {progress?.cefr_level || user?.cefr_level || 'A1'}
            <span className="ml-1.5 opacity-80">· TOEFL {progress?.toefl_approx || user?.toefl_approx || '0-30'}</span>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3">
          <Stat icon={<Flame size={16} weight="fill" />} label="Streak" value={`${progress?.streak_days ?? 0}d`} />
          <Stat icon={<Clock size={16} weight="fill" />} label="Minutes" value={`${progress?.total_minutes ?? 0}`} />
          <Stat icon={<ChatCircleDots size={16} weight="fill" />} label="Sessions" value={`${progress?.total_sessions ?? 0}`} />
        </div>
      </motion.div>

      {error ? <div className="mt-3 text-sm text-[var(--lf-destructive)]">{error}</div> : null}

      {/* Primary actions */}
      <div className="mt-6 grid grid-cols-1 gap-3">
        <ActionCard
          onClick={startTeacherSession}
          disabled={busy}
          title="Teacher Session"
          sub="Guided lesson with vocabulary, pronunciation & Q&A."
          icon={<GraduationCap size={26} weight="duotone" />}
          accent
          testid="home-teacher-btn"
        />
        <ActionCard
          onClick={startFreeTalk}
          disabled={busy}
          title="Free Talk"
          sub="Just chat about anything in your target language."
          icon={<ChatCircleDots size={26} weight="duotone" />}
          testid="home-free-talk-btn"
        />
        <ActionCard
          onClick={() => navigate('/scenarios')}
          title="Guided Scenario"
          sub="Role-play real situations (coffee, interview, directions…)."
          icon={<BookOpenText size={26} weight="duotone" />}
          testid="home-scenarios-btn"
        />
        <ActionCard
          onClick={() => navigate('/repeat')}
          title="Repeat Practice"
          sub="Say short phrases after the AI. Great for beginners."
          icon={<Repeat size={26} weight="duotone" />}
          testid="home-repeat-btn"
        />
      </div>

      {/* Today's recommendation */}
      <div className="mt-6 lf-card overflow-hidden">
        <div className="p-5">
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Today's suggestion</div>
          <div className="mt-1 text-lg font-semibold">Try a 5-minute Free Talk</div>
          <div className="mt-1 text-sm text-[var(--lf-text-muted)]">
            Short, warm sessions build the habit. You've got this.
          </div>
          <button onClick={startFreeTalk} disabled={busy} className="lf-btn lf-btn-secondary mt-4" data-testid="home-today-btn">
            Start now <CaretRight size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* Word of the Day — a little instruction every time they open the app. */}
      <WordOfTheDay />

      {/* Sound of the Week — rotating pronunciation drill. */}
      <SoundOfTheWeek />

      {/* Quick nav */}
      <div className="mt-6 grid grid-cols-2 gap-3">
        <Link to="/vocabulary" className="lf-card flex items-center gap-3 p-4" data-testid="home-vocab-link">
          <div className="grid h-10 w-10 place-items-center rounded-full bg-[var(--lf-surface-muted)] text-[var(--lf-primary)]"><Translate size={20} weight="duotone" /></div>
          <div>
            <div className="font-semibold">Vocabulary</div>
            <div className="text-xs text-[var(--lf-text-muted)]">
              {(progress?.vocab_counts?.new ?? 0) + (progress?.vocab_counts?.learning ?? 0)} to review
            </div>
          </div>
        </Link>
        <Link to="/profile" className="lf-card flex items-center gap-3 p-4" data-testid="home-progress-link">
          <div className="grid h-10 w-10 place-items-center rounded-full bg-[var(--lf-surface-muted)] text-[var(--lf-primary)]"><Trophy size={20} weight="duotone" /></div>
          <div>
            <div className="font-semibold">Progress</div>
            <div className="text-xs text-[var(--lf-text-muted)]">See your growth</div>
          </div>
        </Link>
      </div>

      {/* Recent sessions */}
      {sessions.length > 0 && (
        <div className="mt-8">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Recent sessions</div>
          </div>
          <div className="space-y-2">
            {sessions.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(s.review_ready ? `/review/${s.id}` : `/session/${s.id}`)}
                className="lf-card flex w-full items-center justify-between p-3 text-left"
                data-testid={`home-session-${s.id}`}
              >
                <div>
                  <div className="font-semibold text-sm">{s.mode === 'scenario' ? 'Guided Scenario' : 'Free Talk'}</div>
                  <div className="text-xs text-[var(--lf-text-muted)]">
                    {s.review_ready ? 'Review ready' : 'In progress'} · {new Date(s.started_at).toLocaleDateString()}
                  </div>
                </div>
                <CaretRight size={16} className="text-[var(--lf-text-muted)]" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="h-10" />
      <FirstRunTour open={showTour} onDone={finishTour} />
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="rounded-xl bg-[var(--lf-surface-muted)] p-3 text-center">
      <div className="inline-flex items-center gap-1 text-xs text-[var(--lf-text-muted)]">{icon}{label}</div>
      <div className="mt-1 text-xl font-bold">{value}</div>
    </div>
  );
}

function ActionCard({ onClick, title, sub, icon, accent, testid, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`lf-card flex w-full items-center gap-4 p-5 text-left transition-all duration-200 hover:-translate-y-0.5 ${accent ? 'bg-gradient-to-br from-[#FFFFFF] to-[#F7EFE9]' : ''}`}
    >
      <div className={`grid h-14 w-14 place-items-center rounded-2xl ${accent ? 'bg-[var(--lf-primary)] text-white' : 'bg-[var(--lf-surface-muted)] text-[var(--lf-primary)]'}`}>
        {icon}
      </div>
      <div className="flex-1">
        <div className="text-lg font-bold">{title}</div>
        <div className="text-sm text-[var(--lf-text-muted)]">{sub}</div>
      </div>
      <CaretRight size={20} className="text-[var(--lf-text-muted)]" />
    </button>
  );
}
